import React, { useState } from 'react';
import { Scale, Loader2 } from 'lucide-react';
import { fiatService } from '../../../../services/api';
import { toErrorMessage } from '../../../../utils/safeStr';
import { TRIAGE, STYLES } from '../copy';
import {
    TRIAGE_EXAMPLE_JSON,
    TRIAGE_DEFAULT_TAINTED_UTRS,
    TRIAGE_DEFAULT_ACCOUNT,
    TRIAGE_DEFAULT_CLOSING,
} from '../examples';
import type { OffRampToolPanelProps } from '../types';
import { TierBadge } from '../shared/TierBadge';
import { LegTagBadge } from '../shared/LegTagBadge';
import { ResultEmptyHint } from '../shared/ResultEmptyHint';

export function FrozenTriagePanel({
    className,
    emphasizeExample,
    onComplete,
}: OffRampToolPanelProps) {
    const [eventsText, setEventsText] = useState('');
    const [taintedUtrsText, setTaintedUtrsText] = useState(TRIAGE_DEFAULT_TAINTED_UTRS);
    const [triageAccount, setTriageAccount] = useState(TRIAGE_DEFAULT_ACCOUNT);
    const [closingBalance, setClosingBalance] = useState(TRIAGE_DEFAULT_CLOSING);
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<any>(null);
    const [error, setError] = useState<string | null>(null);

    const runTriage = async (text: string) => {
        let events: any[];
        try {
            events = JSON.parse(text || '[]');
            if (!Array.isArray(events)) throw new Error('Expected a JSON array of legs.');
        } catch (e) {
            setError(`Invalid JSON: ${toErrorMessage(e)}`);
            return;
        }
        const tainted = taintedUtrsText
            .split(/[\s,]+/)
            .map((s) => s.trim())
            .filter(Boolean);
        if (!tainted.length) {
            setError('Enter at least one NCRP tainted UTR.');
            return;
        }
        setLoading(true);
        setError(null);
        setResult(null);
        try {
            const closing = closingBalance.trim() === '' ? undefined : Number(closingBalance);
            const res = await fiatService.triage({
                events,
                tainted_utrs: tainted,
                account_ref: triageAccount || undefined,
                closing_balance:
                    closing !== undefined && !Number.isNaN(closing) ? closing : undefined,
            });
            setResult(res);
            onComplete?.();
        } catch (e) {
            setError(toErrorMessage(e));
        } finally {
            setLoading(false);
        }
    };

    const tryExample = () => {
        setEventsText(TRIAGE_EXAMPLE_JSON);
        setTaintedUtrsText(TRIAGE_DEFAULT_TAINTED_UTRS);
        setTriageAccount(TRIAGE_DEFAULT_ACCOUNT);
        setClosingBalance(TRIAGE_DEFAULT_CLOSING);
        void runTriage(TRIAGE_EXAMPLE_JSON);
    };

    return (
        <div className={`${STYLES.card} ${className ?? ''}`}>
            <ResultEmptyHint>{TRIAGE.empty}</ResultEmptyHint>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                    <label className={STYLES.label}>Account ref</label>
                    <input
                        className={STYLES.input}
                        value={triageAccount}
                        onChange={(e) => setTriageAccount(e.target.value)}
                    />
                </div>
                <div>
                    <label className={STYLES.label}>Tainted UTR(s)</label>
                    <input
                        className={STYLES.input}
                        placeholder="412345678901, …"
                        value={taintedUtrsText}
                        onChange={(e) => setTaintedUtrsText(e.target.value)}
                    />
                </div>
                <div>
                    <label className={STYLES.label}>Closing balance (optional)</label>
                    <input
                        className={STYLES.input}
                        value={closingBalance}
                        onChange={(e) => setClosingBalance(e.target.value)}
                    />
                </div>
            </div>
            <textarea
                className={`${STYLES.input} font-mono text-[11px] h-36 resize-y`}
                placeholder="Statement legs JSON…"
                value={eventsText}
                onChange={(e) => setEventsText(e.target.value)}
                aria-label="Triage statement legs JSON"
            />
            <details className="text-[11px] text-muted-foreground">
                <summary className="cursor-pointer hover:text-foreground">How taint works</summary>
                <p className="mt-1">{TRIAGE.advancedNote}</p>
            </details>
            <div className="flex items-center gap-3 flex-wrap">
                <button
                    type="button"
                    className={emphasizeExample ? STYLES.btn : STYLES.btnSecondary}
                    onClick={tryExample}
                    disabled={loading}
                >
                    {loading && emphasizeExample ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Scale className="w-4 h-4" />
                    )}
                    {TRIAGE.tryExample}
                </button>
                <button
                    type="button"
                    className={emphasizeExample ? STYLES.btnSecondary : STYLES.btn}
                    onClick={() => void runTriage(eventsText)}
                    disabled={loading}
                >
                    {loading && !emphasizeExample ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                        <Scale className="w-4 h-4" />
                    )}
                    {TRIAGE.run}
                </button>
                {error && <span className="text-xs text-red-500">{error}</span>}
            </div>
            {result && (
                <div className="space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                        <TierBadge determinism={result.determinism} />
                        <span className="text-xs font-medium text-foreground">
                            {TRIAGE.cleanReleasable}:{' '}
                            <span className="text-emerald-600 dark:text-emerald-400 font-semibold">
                                ₹
                                {Number(result.totals?.clean_balance_releasable ?? 0).toLocaleString(
                                    'en-IN',
                                )}
                            </span>
                        </span>
                        <span className="text-[11px] text-muted-foreground">
                            tainted remaining ₹
                            {Number(result.totals?.tainted_balance_remaining ?? 0).toLocaleString(
                                'en-IN',
                            )}
                            {' · '}in ₹
                            {Number(result.totals?.tainted_inflow_total ?? 0).toLocaleString('en-IN')}
                            {' · '}out ₹
                            {Number(result.totals?.tainted_outflow_total ?? 0).toLocaleString('en-IN')}
                        </span>
                    </div>
                    {Array.isArray(result.legs) &&
                        result.legs.map((leg: any) => (
                            <div
                                key={leg.event_id}
                                className="border border-border rounded-md p-3 text-xs flex items-start gap-2 flex-wrap"
                            >
                                <LegTagBadge tag={leg.tag} />
                                <span className="font-mono text-[11px]">{leg.direction}</span>
                                <span className="font-semibold">
                                    ₹{Number(leg.amount).toLocaleString('en-IN')}
                                </span>
                                {leg.tainted_portion > 0 && (
                                    <span className="text-amber-700 dark:text-amber-300">
                                        tainted ₹
                                        {Number(leg.tainted_portion).toLocaleString('en-IN')}
                                    </span>
                                )}
                                <span className="text-muted-foreground font-mono text-[10px]">
                                    UTR {leg.utr || '—'}
                                </span>
                                {leg.counterparty_ref && (
                                    <span className="text-muted-foreground">{leg.counterparty_ref}</span>
                                )}
                            </div>
                        ))}
                    {Array.isArray(result.notes) && result.notes.length > 0 && (
                        <div className="text-[11px] text-muted-foreground space-y-0.5">
                            {result.notes.map((n: string, i: number) => (
                                <div key={i}>{n}</div>
                            ))}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}
